import { isFunction } from 'lodash';
import BaseService from '../../common/BaseService';
import WidgetClient from '../../clients/WidgetClient';
import widgetTransformer from './widgetTransformer';

class WidgetService extends BaseService {
  constructor({
    name = 'WidgetService',
    client = WidgetClient,
  }) {
    super({ name });
    this.WidgetClient = client;
    this.widgetConnectionClient = null;
    this.widgetViewClient = null;
    this.widgetLayoutClient = null;
  }

  async get(client, entity, modifier = null) {
    const settings = {
      method: this.WidgetClient.HTTP_METHODS.GET,
    };
    let response;
    try {
      response = await this.sendClientRequest({
        client: isFunction(client) ? client() : client,
        settings,
        errorMsg: `Couldn't fetch ${entity}`,
      });
      if (isFunction(modifier)) {
        response = modifier(response);
      }
    } catch (error) {
      response = Promise.reject(error);
      this.logError(`Error fetching ${entity}: ${error}`);
    }
    return response;
  }

  async delete(client, errorMsg, entity) {
    const settings = {
      method: this.WidgetClient.HTTP_METHODS.DELETE,
    };
    let response;
    try {
      response = await this.sendClientRequest({
        client: isFunction(client) ? client() : client,
        settings,
        errorMsg,
      });
    } catch (error) {
      response = Promise.reject(error);
      this.logError(`Error calling ${entity}: ${error}`);
    }
    return response;
  }

  async getConnection() {
    const client = () => {
      if (!this.widgetConnectionClient) {
        this.widgetConnectionClient = createWidgetConnectionClient(this.WidgetClient);
      }
      return this.widgetConnectionClient;
    };
    const modifier = response => widgetTransformer.transformWidgetConnectionResponse(response);

    return this.get(client, 'widgetConnectionResponse', modifier);
  }

  async getConnectionById() {
    const connectionResponse = await this.getConnection();
    return connectionResponse;
  }

  /**
   * Override this method to use your own specific testing implementation.
   *
   * @returns {Promise<boolean>}
   */
  async testConnection() {
    try {
      await this.getConnection();
      return true;
    } catch (error) {
      this.logError(`Widget Connection did not work! ${error}`);
    }
    return false;
  }

  async getView(viewId) {
    const client = () => {
      this.widgetViewClient = createWidgetViewClient(this.WidgetClient, viewId);
      return this.widgetViewClient;
    };
    const modifier = response => widgetTransformer.transformWidgetViewResponse(response);

    return this.get(client, 'widgetViewResponse', modifier);
  }

  async getLayout(viewId, layoutId) {
    const client = () => {
      this.widgetLayoutClient = createWidgetLayoutClient(this.WidgetClient, viewId, layoutId);
      return this.widgetLayoutClient;
    };
    const modifier = response => widgetTransformer.transformWidgetViewResponse(response);

    return this.get(client, 'widgetViewResponse', modifier);
  }

  async saveWidgetConnection(requestBody) {
    const widgetConnectionSettings = widgetTransformer.transformSaveWidgetConnectionRequest(requestBody);
    let widgetConnectionResponse;
    try {
      if (!this.widgetConnectionClient) {
        this.widgetConnectionClient = createWidgetConnectionClient(this.WidgetClient);
      }
      widgetConnectionResponse = await this.sendClientRequest({
        client: this.widgetConnectionClient,
        settings: widgetConnectionSettings,
        errorMsg: 'Couldn\'t save connection config',
      });
    } catch (error) {
      widgetConnectionResponse = Promise.reject(error);
      this.logError(`Error saving connection config: ${error}`);
    }
    return widgetConnectionResponse;
  }

  async updateWidgetConnection(requestBody) {
    const widgetConnectionSettings = widgetTransformer.transformUpdateWidgetConnectionRequest(requestBody);
    let widgetConnectionResponse;
    try {
      if (!this.widgetConnectionClient) {
        this.widgetConnectionClient = createWidgetConnectionClient(this.WidgetClient);
      }
      widgetConnectionResponse = await this.sendClientRequest({
        client: this.widgetConnectionClient,
        settings: widgetConnectionSettings,
        errorMsg: 'Couldn\'t update connection config',
      });
    } catch (error) {
      widgetConnectionResponse = Promise.reject(error);
      this.logError(`Error updating connection config: ${error}`);
    }
    return widgetConnectionResponse;
  }

  async saveWidgetView(requestBody, viewId) {
    const widgetViewSettings = widgetTransformer.transformSaveWidgetViewRequest(requestBody);
    let widgetViewResponse;
    try {
      this.widgetViewClient = createWidgetViewClient(this.WidgetClient, viewId);
      widgetViewResponse = await this.sendClientRequest({
        client: this.widgetViewClient,
        settings: widgetViewSettings,
        errorMsg: 'Couldn\'t save view config',
      });
    } catch (error) {
      widgetViewResponse = Promise.reject(error);
      this.logError(`Error saving view config: ${error}`);
    }
    return widgetViewResponse;
  }

  async saveWidgetLayout(requestBody, viewId, layoutId) {
    const widgetLayoutSettings = widgetTransformer.transformSaveWidgetLayoutRequest(requestBody);
    let widgetLayoutResponse;
    try {
      this.widgetLayoutClient = createWidgetLayoutClient(this.WidgetClient, viewId, layoutId);
      widgetLayoutResponse = await this.sendClientRequest({
        client: this.widgetLayoutClient,
        settings: widgetLayoutSettings,
        errorMsg: 'Couldn\'t save layout config',
      });
    } catch (error) {
      widgetLayoutResponse = Promise.reject(error);
      this.logError(`Error saving layout config: ${error}`);
    }
    return widgetLayoutResponse;
  }

  async getToSAgreement() {
    const tosAgreementSettings = widgetTransformer.transformGetToSAgreementRequest();
    let tosAgreementResponse;
    try {
      const tosClient = createToSClient(this.WidgetClient);
      tosAgreementResponse = await this.sendClientRequest({
        client: tosClient,
        settings: tosAgreementSettings,
        errorMsg: 'Couldn\'t get tos agreement',
      });
    } catch (error) {
      tosAgreementResponse = Promise.reject(error);
      this.logError(`Error getting tos agreement: ${error}`);
    }
    return tosAgreementResponse;
  }

  async agreeToToS(id) {
    const tosAgreementSettings = widgetTransformer.transformPostToSAgreementRequest();
    try {
      const tosClient = createPutToSClient(this.WidgetClient, id);
      await this.sendClientRequest({
        client: tosClient,
        settings: tosAgreementSettings,
        errorMsg: 'Couldn\'t save agreement ToS',
      });
    } catch (error) {
      this.logError(`Error save agreement ToS: ${error}`);
      return false;
    }
    return true;
  }
}

export function createClient(name, contextName, resource, Client, values = {}) {
  return new Client({
    resource,
    contextName,
    ...values,
  });
}

function createToSClient(Client) {
  return createClient(
    'widgetToSClient',
    'GetWidgetToSAgreement',
    Client.RESOURCES.GET_WIDGET_TOS,
    Client,
  );
}

function createPutToSClient(Client, id) {
  return createClient(
    'widgetToSClient',
    'PuttWidgetToSAgreement',
    Client.RESOURCES.POST_WIDGET_TOS,
    Client,
    { tosId: id },
  );
}

function createWidgetConnectionClient(Client) {
  return createClient(
    'widgetConnectionClient',
    'GetWidgetConnectionConfiguration',
    Client.RESOURCES.WIDGET_CONNECTION,
    Client,
  );
}

function createWidgetViewClient(Client, viewId) {
  return createClient(
    'createWidgetViewClient',
    'GetWidgetViewConfiguration',
    Client.RESOURCES.WIDGET_VIEW,
    Client,
    { viewId },
  );
}

function createWidgetLayoutClient(Client, viewId, layoutId) {
  return createClient(
    'createWidgetLayoutClient',
    'GetWidgetLayoutConfiguration',
    Client.RESOURCES.WIDGET_LAYOUT,
    Client,
    { viewId, layoutId },
  );
}

const widgetService = new WidgetService({});
export { widgetService };

export default WidgetService;
